import React, { useState, useEffect, useCallback } from "react";
import { type LifecycleState, STATE_LABELS, STATE_COLORS } from "./lifecycle";
import ApprovalSection, { useApprovalStatus } from "./ApprovalSection";
import { useModal } from "../ui/Modal";

interface DocumentInfo {
  id: string;
  name: string;
  state: LifecycleState;
  part_number: string;
  revision: string;
  rejection_reason?: string;
}

interface HistoryEntry {
  id: string;
  user_id: string | null;
  user_name?: string;
  old_state: LifecycleState | null;
  new_state: LifecycleState;
  reason: string | null;
  created_at: string;
}

interface WhereUsedEntry {
  id: string;
  name: string;
}

function errMsg(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

interface ReleasePanelProps {
  documentId: string;
  onStateChange?: () => void;
}

const TRANSITIONS: Record<LifecycleState, { label: string; target: LifecycleState; variant: string }[]> = {
  wip: [{ label: "Submit for Review", target: "review", variant: "primary" }],
  review: [
    { label: "Approve & Release", target: "released", variant: "success" },
    { label: "Reject (Back to WIP)", target: "wip", variant: "danger" },
  ],
  released: [{ label: "Mark Obsolete", target: "obsolete", variant: "danger" }],
  obsolete: [],
};

export default function ReleasePanel({ documentId, onStateChange }: ReleasePanelProps) {
  const modal = useModal();
  const [doc, setDoc] = useState<DocumentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const [editPartNumber, setEditPartNumber] = useState("");
  const [editRevision, setEditRevision] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const { total: approvalTotal, approved: approvalApproved, allApproved: approvalAllApproved } = useApprovalStatus(documentId);

  const fetchDoc = useCallback(async () => {
    try {
      const [docRes, histRes] = await Promise.all([
        fetch(`/api/documents/${documentId}`),
        fetch(`/api/documents/${documentId}/history`),
      ]);
      if (!docRes.ok) throw new Error("Failed to load document");
      const data = await docRes.json();
      setDoc(data);
      setEditPartNumber(data.part_number || "");
      setEditRevision(data.revision || "");
      setDirty(false);
      setError("");

      if (histRes.ok) {
        setHistory(await histRes.json());
      }
    } catch (err: unknown) {
      setError(errMsg(err, "Failed to load document"));
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    fetchDoc();
  }, [fetchDoc]);

  const handleTransition = useCallback(
    async (targetState: LifecycleState) => {
      const label = STATE_LABELS[targetState];

      if (targetState === "obsolete") {
        try {
          const res = await fetch(`/api/bom/${documentId}/where-used`);
          if (res.ok) {
            const usage: WhereUsedEntry[] = await res.json();
            if (usage.length > 0) {
              const names = usage.map((u) => u.name).join(", ");
              if (!(await modal.confirm(`WARNING: This part is used in ${usage.length} assemblies (${names}). Obsoleting it will break these BOMs. Continue?`))) {
                return;
              }
            } else {
              if (!(await modal.confirm(`Transition this document to "${label}"?`))) return;
            }
          }
        } catch {
          if (!(await modal.confirm(`Transition this document to "${label}"?`))) return;
        }
      } else {
        if (!(await modal.confirm(`Transition this document to "${label}"?`))) return;
      }

      let reason: string | undefined;
      if (targetState === "wip" && doc?.state === "review") {
        const input = window.prompt("Reason for rejection (optional):");
        if (input !== null) reason = input.trim() || undefined;
      }

      setTransitioning(true);
      setError("");
      try {
        const res = await fetch(`/api/documents/${documentId}/transition`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state: targetState, reason }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Transition failed (${res.status})`);
        }
        const updated = await res.json();
        setDoc(updated);
        onStateChange?.();
      } catch (err: unknown) {
        setError(errMsg(err, "Transition failed"));
      } finally {
        setTransitioning(false);
      }
    },
    [documentId, onStateChange, doc]
  );

  const handleSaveFields = useCallback(async () => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`/api/documents/${documentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          part_number: editPartNumber,
          revision: editRevision,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Save failed (${res.status})`);
      }
      const updated = await res.json();
      setDoc(updated);
      setEditPartNumber(updated.part_number || "");
      setEditRevision(updated.revision || "");
      setDirty(false);
    } catch (err: unknown) {
      setError(errMsg(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }, [documentId, editPartNumber, editRevision]);

  if (loading) {
    return (
      <div className="release-panel">
        <div className="release-loading">Loading...</div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="release-panel">
        <div className="release-error">{error || "Document not found"}</div>
      </div>
    );
  }

  const actions = TRANSITIONS[doc.state] || [];
  const colors = STATE_COLORS[doc.state];

  const STEPS: LifecycleState[] = ["wip", "review", "released", "obsolete"];
  const stepIndex = STEPS.indexOf(doc.state);

  return (
    <div className="release-panel">
      {/* Rejection callout */}
      {doc.state === "wip" && doc.rejection_reason && (
        <div className="release-rejection-callout">
          <strong>Returned to WIP</strong> — {doc.rejection_reason}
        </div>
      )}

      {/* Lifecycle stepper */}
      <div className="release-state-section">
        <div className="release-section-header">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="8" cy="8" r="5" />
            <path d="M8 5v3l2 1" />
          </svg>
          Lifecycle State
        </div>
        <div className="lifecycle-stepper">
          {STEPS.map((step, i) => {
            const isDone = i < stepIndex;
            const isActive = i === stepIndex;
            const stepColors = STATE_COLORS[step];
            return (
              <React.Fragment key={step}>
                <div className="lifecycle-step">
                  <div
                    className={`lifecycle-step-circle${isDone ? " lifecycle-step-circle--done" : isActive ? " lifecycle-step-circle--active" : ""}`}
                    style={isActive ? { borderColor: stepColors.fg, background: stepColors.bg, color: stepColors.fg } : undefined}
                  >
                    {isDone ? (
                      <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
                        <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className={`lifecycle-step-label${isActive ? " lifecycle-step-label--active" : ""}`}>
                    {STATE_LABELS[step]}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`lifecycle-connector${isDone ? " lifecycle-connector--done" : ""}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Part number + revision fields */}
      <div className="release-fields">
        <div className="release-section-header">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M2 4h12M2 8h8M2 12h10" />
          </svg>
          Properties
        </div>
        <div className="release-field">
          <label className="release-field-label">Part Number</label>
          <input
            className="release-field-input"
            type="text"
            value={editPartNumber}
            placeholder="e.g. ASSY-001"
            onChange={(e) => {
              setEditPartNumber(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="release-field">
          <label className="release-field-label">Revision</label>
          <input
            className="release-field-input"
            type="text"
            value={editRevision}
            placeholder="e.g. A"
            onChange={(e) => {
              setEditRevision(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        {dirty && (
          <button
            className="release-save-btn"
            onClick={handleSaveFields}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Properties"}
          </button>
        )}
      </div>

      {/* Approvals */}
      <ApprovalSection resourceType="document" resourceId={documentId} />

      {/* Error */}
      {error && <div className="release-error">{error}</div>}

      {/* Transition actions */}
      {actions.length > 0 && (
        <div className="release-actions">
          <div className="release-section-header">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M5 3l6 5-6 5" />
            </svg>
            Actions
          </div>
          {actions.map((action) => {
            const isReleaseBlocked =
              action.target === "released" &&
              approvalTotal > 0 &&
              !approvalAllApproved;
            return (
              <button
                key={action.target}
                className={`release-action-btn release-action-btn--${action.variant}`}
                onClick={() => handleTransition(action.target)}
                disabled={transitioning || isReleaseBlocked}
                title={isReleaseBlocked ? `Awaiting approvals (${approvalApproved}/${approvalTotal})` : undefined}
              >
                {transitioning
                  ? "..."
                  : isReleaseBlocked
                  ? `Awaiting approvals (${approvalApproved}/${approvalTotal})`
                  : action.label}
              </button>
            );
          })}
        </div>
      )}

      {doc.state === "obsolete" && (
        <div className="release-terminal">
          This document is obsolete. No further transitions are available.
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="release-history">
          <div className="release-section-header">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8 3v5l3 2M14 8a6 6 0 11-12 0 6 6 0 0112 0z" />
            </svg>
            History
          </div>
          <div className="history-list">
            {history.map((h) => (
              <div key={h.id} className="history-item">
                <div className="history-item-header">
                  <span className="history-user">{h.user_name || "Unknown"}</span>
                  <span className="history-date">{new Date(h.created_at).toLocaleString()}</span>
                </div>
                <div className="history-transition">
                  <span className="history-state" style={{ color: h.old_state ? STATE_COLORS[h.old_state]?.fg : undefined }}>{h.old_state ? STATE_LABELS[h.old_state] : "—"}</span>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
                    <path d="M3 8h10M10 5l3 3-3 3" />
                  </svg>
                  <span className="history-state" style={{ color: STATE_COLORS[h.new_state]?.fg }}>{STATE_LABELS[h.new_state]}</span>
                </div>
                {h.reason && <div className="history-reason">"{h.reason}"</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
